import { useEffect, useRef } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { Markdown } from "tiptap-markdown";
import {
  Bold, Italic, Strikethrough, Code, Heading1, Heading2, Heading3,
  List, ListOrdered, ListChecks, Quote, Undo2, Redo2,
} from "lucide-react";
import "./richtext.css";

/**
 * Notion-style rich-text editor built on TipTap. It renders formatted text live
 * (WYSIWYG) with a visible formatting toolbar. Markdown is the value in/out, so
 * it drops in anywhere the app previously used a Markdown textarea while keeping
 * storage, export, and the read-only renderer unchanged.
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = "5rem",
}: {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  minHeight?: string;
}) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: placeholder ?? "Write… the toolbar formats your text, or use Markdown shortcuts.",
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Markdown.configure({ html: false, linkify: true, transformPastedText: true }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChangeRef.current(editor.storage.markdown.getMarkdown()),
    editorProps: {
      attributes: { class: "tiptap", style: `min-height:${minHeight}` },
    },
  });

  // Reflect external value changes (AI draft/expand, loading a new item) without
  // clobbering the caret while typing — resets only when the editor isn't focused.
  useEffect(() => {
    if (!editor) return;
    if (editor.isFocused) return;
    const current = editor.storage.markdown.getMarkdown();
    if (value !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-bg transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
      {editor && <Toolbar editor={editor} />}
      <div className="px-3 py-2">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  // Re-read on every render; @tiptap/react re-renders this on each transaction,
  // so isActive() reflects the current selection.
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-surface-2/40 px-1.5 py-1">
      <Btn editor={editor} label="Bold (Ctrl+B)" active={editor.isActive("bold")} run={(c) => c.toggleBold()}><Bold className="h-3.5 w-3.5" /></Btn>
      <Btn editor={editor} label="Italic (Ctrl+I)" active={editor.isActive("italic")} run={(c) => c.toggleItalic()}><Italic className="h-3.5 w-3.5" /></Btn>
      <Btn editor={editor} label="Strikethrough" active={editor.isActive("strike")} run={(c) => c.toggleStrike()}><Strikethrough className="h-3.5 w-3.5" /></Btn>
      <Btn editor={editor} label="Inline code" active={editor.isActive("code")} run={(c) => c.toggleCode()}><Code className="h-3.5 w-3.5" /></Btn>
      <Sep />
      <Btn editor={editor} label="Heading 1" active={editor.isActive("heading", { level: 1 })} run={(c) => c.toggleHeading({ level: 1 })}><Heading1 className="h-3.5 w-3.5" /></Btn>
      <Btn editor={editor} label="Heading 2" active={editor.isActive("heading", { level: 2 })} run={(c) => c.toggleHeading({ level: 2 })}><Heading2 className="h-3.5 w-3.5" /></Btn>
      <Btn editor={editor} label="Heading 3" active={editor.isActive("heading", { level: 3 })} run={(c) => c.toggleHeading({ level: 3 })}><Heading3 className="h-3.5 w-3.5" /></Btn>
      <Sep />
      <Btn editor={editor} label="Bullet list" active={editor.isActive("bulletList")} run={(c) => c.toggleBulletList()}><List className="h-3.5 w-3.5" /></Btn>
      <Btn editor={editor} label="Numbered list" active={editor.isActive("orderedList")} run={(c) => c.toggleOrderedList()}><ListOrdered className="h-3.5 w-3.5" /></Btn>
      <Btn editor={editor} label="Checklist" active={editor.isActive("taskList")} run={(c) => c.toggleList("taskList", "taskItem")}><ListChecks className="h-3.5 w-3.5" /></Btn>
      <Btn editor={editor} label="Quote" active={editor.isActive("blockquote")} run={(c) => c.toggleBlockquote()}><Quote className="h-3.5 w-3.5" /></Btn>
      <Sep />
      <Btn editor={editor} label="Undo" run={(c) => c.undo()} disabled={!editor.can().undo()}><Undo2 className="h-3.5 w-3.5" /></Btn>
      <Btn editor={editor} label="Redo" run={(c) => c.redo()} disabled={!editor.can().redo()}><Redo2 className="h-3.5 w-3.5" /></Btn>
    </div>
  );
}

function Sep() {
  return <div className="mx-0.5 h-4 w-px bg-border" />;
}

function Btn({
  editor, label, active = false, disabled = false, run, children,
}: {
  editor: Editor;
  label: string;
  active?: boolean;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  run: (chain: any) => any;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      // Keep the editor focused so the command applies to the current selection.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => run(editor.chain().focus()).run()}
      className={[
        "grid h-7 w-7 place-items-center rounded-md transition-colors disabled:opacity-30",
        active ? "bg-primary/15 text-primary" : "text-muted hover:bg-surface-2 hover:text-text",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
